import { CONTENTFUL_TAGS } from '@/core/constants/contentfulTags';
import { logger } from '@/core/logger';
import { getSdkClient, mapAppLocaleToContentful } from '@/core/services/contentfulClient';

export interface FaqItem {
    category: string;
    question: string;
    answer: string;
}

export interface FaqCategoryGroup {
    category: string;
    items: FaqItem[];
}

const PAGE_LIMIT = 100;

function groupFaqItems(allItems: any[], locale: string, source: string): FaqCategoryGroup[] {
    if (allItems.length === 0) {
        logger.warn('FAQ', 'No FAQ entries returned', { locale, source });
    }

    const grouped = allItems.reduce<Record<string, FaqItem[]>>((acc, item) => {
        const category = item.fields?.category ?? 'Other';
        const question = item.fields?.question ?? '';
        const answer = item.fields?.answer ?? '';
        const group = acc[category] ?? (acc[category] = []);
        group.push({ category, question, answer });
        return acc;
    }, {});

    const categoryKeys = Object.keys(grouped);
    logger.info('FAQ', 'Grouped FAQs', {
        locale,
        source,
        categories: categoryKeys.length,
        categoryNames: categoryKeys,
        total: allItems.length,
    });

    return Object.entries(grouped)
        .map(([category, items]) => ({
            category,
            items,
        }))
        .sort((a, b) => a.category.localeCompare(b.category));
}

async function fetchFaqEntries(
    locale: 'en' | 'ar',
    source: string,
    tagQuery: Record<string, string>,
): Promise<FaqCategoryGroup[]> {
    const client = getSdkClient();
    const cfLocale = mapAppLocaleToContentful(locale);

    if (!client) {
        logger.warn('FAQ', 'Contentful client unavailable', { source });
        return [];
    }

    logger.info('FAQ', 'Fetching FAQs from Contentful', {
        locale: cfLocale,
        source,
        tagQuery,
    });

    let allItems: any[] = [];
    let skip = 0;
    let total = 0;

    try {
        do {
            const res = await client.getEntries({
                content_type: 'faq',
                locale: cfLocale,
                // category is Symbol (sortable); question is Text (not sortable) so use createdAt for stable ordering
                order: ['fields.category', '-sys.createdAt'],
                limit: PAGE_LIMIT,
                skip,
                include: 0,
                ...tagQuery,
            });

            total = res.total || 0;
            allItems = allItems.concat(res.items || []);
            skip += PAGE_LIMIT;

            logger.info('FAQ', 'Fetched FAQ page', {
                locale: cfLocale,
                source,
                fetched: res.items?.length ?? 0,
                total,
                nextSkip: skip,
            });
        } while (skip < total);
    } catch (error: any) {
        logger.error('FAQ', 'Failed to fetch FAQs', {
            locale: cfLocale,
            source,
            error: error?.message || error,
        });
        return [];
    }

    return groupFaqItems(allItems, cfLocale, source);
}

/** FAQs for normal login. Excludes entries tagged adha. */
export async function fetchFaqs(locale: 'en' | 'ar'): Promise<FaqCategoryGroup[]> {
    return fetchFaqEntries(locale, 'standard', {
        'metadata.tags.sys.id[nin]': CONTENTFUL_TAGS.ADHA,
    });
}

/** FAQs for the ADHA journey. Returns only entries tagged adha. */
export async function fetchAdhaFaqs(locale: 'en' | 'ar'): Promise<FaqCategoryGroup[]> {
    return fetchFaqEntries(locale, 'adha', {
        'metadata.tags.sys.id[in]': CONTENTFUL_TAGS.ADHA,
    });
}

